import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { AdPlacement, AD_PLACEMENTS } from '../../components/ads';
import { useStoreBranding } from '../../hooks/useStoreBranding';
import { useGA4 } from '../../contexts/GA4Context';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import '../../themes/edition/EditionAbout.css';
import '../../themes/atelier/AtelierAbout.css';
import '../../themes/gallery/GalleryAbout.css';

// SVG icon map
const VALUE_ICONS = {
  star: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
  heart: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>,
  check: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  lightbulb: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14"/></svg>,
  shield: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  trophy: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0012 0V2z"/></svg>
};

const DEFAULT_VALUES = [
  { title: 'Quality First', description: 'We never compromise on the quality of our products.', icon: 'star' },
  { title: 'Customer Focus', description: 'Your satisfaction is at the heart of everything we do.', icon: 'heart' },
  { title: 'Innovation', description: 'We constantly evolve to bring you the best.', icon: 'lightbulb' },
  { title: 'Integrity', description: 'Honest and transparent in all our dealings.', icon: 'shield' }
];

const AboutPage = () => {
  const { themeStyles, store, theme, premiumThemeConfig } = useOutletContext() || { themeStyles: {}, store: {}, theme: {} };
  const isEditionTheme = premiumThemeConfig?.theme?.slug === 'edition';
  const isAtelierTheme = premiumThemeConfig?.theme?.slug === 'atelier';
  const isGalleryTheme = premiumThemeConfig?.theme?.slug === 'gallery';
  const navigate = useNavigate();

  useStoreBranding(store, 'About');

  // Hero-flush layout — see HomePage for the full pattern. Skipped only
  // while the announcement bar is actively rendered (enabled + text AND
  // not dismissed). The dismissed event from AnnouncementBar re-arms
  // hero-flush without a reload.
  const [announcementDismissed, setAnnouncementDismissed] = useState(() => {
    try {
      return window.sessionStorage?.getItem('sf_announcement_dismissed') === 'true';
    } catch { return false; }
  });
  useEffect(() => {
    const handler = () => setAnnouncementDismissed(true);
    window.addEventListener('sf:announcement-dismissed', handler);
    return () => window.removeEventListener('sf:announcement-dismissed', handler);
  }, []);
  const announcementActive = !!(
    store?.settings?.announcement?.enabled &&
    store?.settings?.announcement?.text &&
    !announcementDismissed
  );
  useEffect(() => {
    if (announcementActive) return undefined;
    document.body.classList.add('sf-hero-flush');
    return () => { document.body.classList.remove('sf-hero-flush'); };
  }, [announcementActive]);

  const ga4 = useGA4() || {};
  const currencyCtx = useStorefrontCurrency() || {};

  // Merge premium theme global styles when available
  const globalOverrides = premiumThemeConfig?.globalOverrides || {};
  const premiumColors = premiumThemeConfig?.theme?.globalSettings?.colors || {};
  const premiumFonts = premiumThemeConfig?.theme?.globalSettings?.fonts || {};

  // Color helpers - prefer premium theme colors over basic themeStyles
  const primaryColor = globalOverrides?.colors?.primary || premiumColors?.primary || themeStyles?.primary || '#0d7463';
  const backgroundColor = globalOverrides?.colors?.background || premiumColors?.background || themeStyles?.background || '#ffffff';
  const borderColor = globalOverrides?.colors?.border || premiumColors?.border || themeStyles?.border || '#e5e7eb';
  const textColor = globalOverrides?.colors?.text || premiumColors?.text || themeStyles?.text || '#1f2937';
  const mutedColor = globalOverrides?.colors?.muted || premiumColors?.muted || themeStyles?.textMuted || '#6b7280';
  const headingFont = premiumFonts?.heading || theme?.fonts?.heading || 'inherit';
  const bodyFont = premiumFonts?.body || theme?.fonts?.primary || 'inherit';

  const aboutTitle = store?.settings?.aboutTitle || store?.aboutTitle || 'Our Story';
  const aboutDescription = store?.settings?.aboutDescription || store?.aboutDescription ||
    'Learn more about our story, mission, and the people behind our success.';
  const aboutContent = store?.settings?.aboutContent || store?.story ||
    'Founded with a mission to provide exceptional products and services that make a real difference in people\'s lives. What began as a small team with big dreams has grown into a trusted brand serving customers worldwide. Today, we continue to innovate and expand our offerings while staying true to our core values of quality, integrity, and customer satisfaction.';
  const aboutImage = store?.settings?.aboutImage || store?.aboutImage;
  const aboutImagePosition = store?.settings?.aboutImagePosition || 'right';
  const aboutCtaText = store?.settings?.aboutCtaText || 'Shop Now';
  const aboutCtaLink = store?.settings?.aboutCtaLink || 'shop';
  const aboutHeroBackground = store?.settings?.aboutHeroBackground;

  const merchantValues = store?.settings?.aboutValues || [];
  const values = merchantValues.length > 0 ? merchantValues : DEFAULT_VALUES;

  const renderIcon = (iconType) => VALUE_ICONS[iconType] || VALUE_ICONS.star;

  const getStorePath = (path = '') => {
    const hostname = window.location.hostname;
    const isOnSubdomain = hostname.includes('.') && !hostname.startsWith('www.');
    if (isOnSubdomain) {
      return path ? `/${path}` : '/';
    }
    return path ? `/s/${store.slug}/${path}` : `/s/${store.slug}`;
  };

  const showAboutPage = store?.settings?.storefrontDisplay?.showAboutPage !== false;

  if (!showAboutPage) {
    return (
      <div style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 1.5rem',
        backgroundColor
      }}>
        <div style={{
          width: '100%',
          maxWidth: '520px',
          padding: '2rem',
          border: `1px solid ${borderColor}`,
          borderRadius: '18px',
          background: '#ffffff',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 1rem',
            borderRadius: '999px',
            backgroundColor: `${primaryColor}12`,
            color: primaryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </div>
          <h1 style={{
            margin: '0 0 0.75rem',
            color: textColor,
            fontFamily: headingFont,
            fontSize: '1.5rem',
            fontWeight: 800
          }}>
            About page is unavailable
          </h1>
          <p style={{
            margin: '0 0 1.5rem',
            color: mutedColor,
            fontFamily: bodyFont,
            lineHeight: 1.6
          }}>
            This store has not enabled its About page.
          </p>
          <button
            type="button"
            onClick={() => navigate(getStorePath('shop'))}
            style={{
              border: 'none',
              borderRadius: '999px',
              backgroundColor: primaryColor,
              color: '#ffffff',
              padding: '0.8rem 1.25rem',
              fontWeight: 700,
              cursor: 'pointer',
              width: 'auto',
              minWidth: 0
            }}
          >
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .abt-wrapper { overflow-x: hidden; }

        /* ─── Hero ─── */
        .abt-hero {
          position: relative;
          padding: 6rem 0 5rem;
          text-align: center;
          overflow: hidden;
        }
        .abt-hero-overlay {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .abt-hero-inner {
          position: relative;
          z-index: 1;
          max-width: 780px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        .abt-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem 1.125rem;
          border-radius: 100px;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 1.75rem;
        }
        .abt-hero-title {
          font-size: 3.25rem;
          font-weight: 800;
          line-height: 1.1;
          margin: 0 0 1.25rem;
          letter-spacing: -0.03em;
        }
        .abt-hero-desc {
          font-size: 1.1875rem;
          line-height: 1.7;
          margin: 0 auto;
          max-width: 600px;
        }
        .abt-hero-divider {
          width: 56px;
          height: 3px;
          border-radius: 3px;
          margin: 2.5rem auto 0;
        }

        /* ─── Story ─── */
        .abt-story {
          padding: 5rem 0;
        }
        .abt-container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        .abt-story-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }
        .abt-story-img-wrap {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 24px 64px -16px rgba(0,0,0,0.14);
        }
        .abt-story-img-wrap img {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 20px;
        }
        .abt-story-img-accent {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 20px;
          border: 2px solid;
          top: 14px;
          left: 14px;
          z-index: -1;
          opacity: 0.25;
        }
        .abt-story-label {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8125rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 1.25rem;
        }
        .abt-story-label-line {
          display: inline-block;
          width: 28px;
          height: 2px;
          border-radius: 2px;
        }
        .abt-story-heading {
          font-size: 2.375rem;
          font-weight: 800;
          line-height: 1.15;
          margin: 0 0 1.5rem;
          letter-spacing: -0.02em;
        }
        .abt-story-text {
          font-size: 1.0625rem;
          line-height: 1.85;
          margin: 0 0 2.25rem;
          white-space: pre-wrap;
        }
        .abt-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.875rem 2.25rem;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          color: #fff;
          transition: all 0.25s ease;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          min-height: 50px;
          font-family: inherit;
        }
        .abt-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(0,0,0,0.18);
        }
        .abt-cta-btn:active { transform: translateY(0); }

        /* ─── Quote (between story and values) ─── */
        .abt-quote-section {
          padding: 3rem 0;
          text-align: center;
        }
        .abt-quote-inner {
          max-width: 720px;
          margin: 0 auto;
          padding: 0 1.5rem;
          position: relative;
        }
        .abt-quote-icon {
          margin-bottom: 1rem;
          opacity: 0.15;
        }
        .abt-quote-text {
          font-size: 1.375rem;
          font-weight: 500;
          line-height: 1.7;
          font-style: italic;
          margin: 0 0 1rem;
        }
        .abt-quote-author {
          font-size: 0.9375rem;
          font-weight: 600;
          margin: 0;
        }

        /* ─── Ad ─── */
        .abt-ad-section {
          max-width: 1140px;
          margin: 0 auto;
          padding: 1rem 1.5rem;
        }

        /* ─── Values ─── */
        .abt-values {
          padding: 5rem 0;
        }
        .abt-values-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }
        .abt-values-heading {
          font-size: 2.375rem;
          font-weight: 800;
          margin: 0 0 0.75rem;
          letter-spacing: -0.02em;
        }
        .abt-values-sub {
          font-size: 1.0625rem;
          line-height: 1.6;
          max-width: 520px;
          margin: 0 auto;
          opacity: 0.7;
        }
        .abt-values-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        .abt-value-card {
          padding: 2.25rem 2rem;
          border-radius: 16px;
          transition: all 0.3s ease;
          border: 1px solid transparent;
          position: relative;
          overflow: hidden;
        }
        .abt-value-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px -10px rgba(0,0,0,0.1);
        }
        .abt-value-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .abt-value-card:hover::before {
          opacity: 1;
        }
        .abt-value-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.25rem;
          transition: transform 0.25s ease;
        }
        .abt-value-card:hover .abt-value-icon {
          transform: scale(1.08);
        }
        .abt-value-title {
          font-size: 1.125rem;
          font-weight: 700;
          margin: 0 0 0.5rem;
        }
        .abt-value-desc {
          font-size: 0.9375rem;
          line-height: 1.7;
          margin: 0;
          opacity: 0.72;
        }

        /* ─── Footer CTA ─── */
        .abt-footer-cta {
          padding: 4rem 0 5rem;
          text-align: center;
        }
        .abt-footer-cta-inner {
          max-width: 800px;
          margin: 0 auto;
          padding: 4rem 2.5rem;
          border-radius: 24px;
          position: relative;
          overflow: hidden;
        }
        .abt-footer-cta-heading {
          font-size: 2.125rem;
          font-weight: 800;
          margin: 0 0 0.75rem;
          position: relative;
          z-index: 1;
          color: #fff;
        }
        .abt-footer-cta-text {
          font-size: 1.0625rem;
          margin: 0 0 2.25rem;
          opacity: 0.9;
          position: relative;
          z-index: 1;
          color: #fff;
          max-width: 480px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }
        .abt-footer-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.875rem 2.5rem;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          background: #fff;
          transition: all 0.25s ease;
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          position: relative;
          z-index: 1;
          min-height: 50px;
          font-family: inherit;
        }
        .abt-footer-cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(0,0,0,0.2);
        }
        .abt-footer-cta-btn:active { transform: translateY(0); }

        /* ─── Responsive: Tablet ─── */
        @media (max-width: 768px) {
          .abt-hero { padding: 4rem 0 3.5rem !important; }
          .abt-hero-title { font-size: 2.25rem !important; }
          .abt-hero-desc { font-size: 1.05rem !important; }
          .abt-story { padding: 3rem 0 !important; }
          .abt-story-grid {
            grid-template-columns: 1fr !important;
            gap: 2.5rem !important;
          }
          .abt-story-img-wrap img { max-height: 400px; object-fit: contain; width: 100%; }
          .abt-story-heading { font-size: 1.875rem !important; }
          .abt-story-text { font-size: 1rem !important; }
          .abt-values { padding: 3rem 0 !important; }
          .abt-values-heading { font-size: 1.875rem !important; }
          .abt-values-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .abt-value-card { padding: 1.75rem 1.5rem !important; }
          .abt-footer-cta { padding: 3rem 0 !important; }
          .abt-footer-cta-inner {
            padding: 3rem 1.75rem !important;
            margin: 0 1rem !important;
          }
          .abt-footer-cta-heading { font-size: 1.625rem !important; }
          .abt-quote-text { font-size: 1.125rem !important; }
        }

        /* ─── Responsive: Mobile ─── */
        @media (max-width: 480px) {
          .abt-hero { padding: 3rem 0 2.5rem !important; }
          .abt-hero-badge { font-size: 0.75rem !important; }
          .abt-hero-title { font-size: 1.75rem !important; }
          .abt-hero-desc { font-size: 0.95rem !important; }
          .abt-story { padding: 2.5rem 0 !important; }
          .abt-container { padding: 0 1rem !important; }
          .abt-story-heading { font-size: 1.5rem !important; }
          .abt-values { padding: 2.5rem 0 !important; }
          .abt-values-heading { font-size: 1.5rem !important; }
          .abt-value-card { padding: 1.25rem 1.125rem !important; }
          .abt-footer-cta { padding: 2.5rem 0 !important; }
          .abt-footer-cta-inner {
            padding: 2.25rem 1.25rem !important;
            border-radius: 16px !important;
          }
          .abt-footer-cta-heading { font-size: 1.375rem !important; }
          .abt-footer-cta-text { font-size: 0.95rem !important; }
          .abt-cta-btn,
          .abt-footer-cta-btn {
            padding: 0.75rem 1.75rem !important;
            font-size: 0.9375rem !important;
          }
          .abt-quote-text { font-size: 1rem !important; }
          .abt-quote-section { padding: 2rem 0 !important; }
        }
      `}</style>

      <div className={`abt-wrapper ${isEditionTheme ? 'edition-about' : ''} ${isAtelierTheme ? 'atelier-about' : ''} ${isGalleryTheme ? 'gallery-about' : ''}`}>

        {/* ── HERO ──
            When a merchant uploads a hero image we cover it (the dark
            overlay below gives text contrast); otherwise we sit on a
            solid themed surface — no soft gradient, no AI slop. */}
        <section
          className="abt-hero"
          style={{
            background: aboutHeroBackground
              ? `url(${aboutHeroBackground}) center/cover no-repeat`
              : backgroundColor,
            color: aboutHeroBackground ? '#fff' : (textColor),
          }}
        >
          {aboutHeroBackground && (
            <div className="abt-hero-overlay" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.62))',
            }} />
          )}

          <div className="abt-hero-inner">
            <div
              className="abt-hero-badge"
              style={{
                background: aboutHeroBackground ? 'rgba(255,255,255,0.12)' : `${primaryColor}0f`,
                color: aboutHeroBackground ? '#fff' : primaryColor,
                backdropFilter: aboutHeroBackground ? 'blur(6px)' : 'none',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              About Us
            </div>

            <h1
              className="abt-hero-title"
              style={{
                color: aboutHeroBackground ? '#fff' : (textColor),
                textShadow: aboutHeroBackground ? '0 2px 12px rgba(0,0,0,0.3)' : 'none',
              }}
            >
              {store?.name || 'Our Company'}
            </h1>

            <p
              className="abt-hero-desc"
              style={{
                color: aboutHeroBackground ? 'rgba(255,255,255,0.9)' : (mutedColor),
                textShadow: aboutHeroBackground ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
              }}
            >
              {aboutDescription}
            </p>

            <div
              className="abt-hero-divider"
              style={{
                background: aboutHeroBackground ? 'rgba(255,255,255,0.35)' : primaryColor,
              }}
            />
          </div>
        </section>

        {/* ── STORY ── */}
        <section
          className="abt-story"
          style={{ backgroundColor: backgroundColor }}
        >
          <div className="abt-container">
            <div className="abt-story-grid" style={{
              direction: aboutImagePosition === 'left' ? 'rtl' : 'ltr',
            }}>
              {/* Text */}
              <div style={{ direction: 'ltr' }}>
                <div className="abt-story-label" style={{ color: primaryColor }}>
                  <span className="abt-story-label-line" style={{ backgroundColor: primaryColor }} />
                  Our Story
                </div>

                <h2 className="abt-story-heading" style={{ color: textColor }}>
                  {aboutTitle}
                </h2>

                <p className="abt-story-text" style={{ color: mutedColor }}>
                  {aboutContent}
                </p>

                {aboutCtaText && (
                  <button
                    className="abt-cta-btn"
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => navigate(getStorePath(aboutCtaLink))}
                  >
                    {aboutCtaText}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Image */}
              {aboutImage && (
                <div style={{ direction: 'ltr', position: 'relative' }}>
                  <div className="abt-story-img-wrap">
                    <img src={aboutImage} alt={store?.name || 'About us'} />
                  </div>
                  <div
                    className="abt-story-img-accent"
                    style={{ borderColor: primaryColor }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── AD PLACEMENT ── */}
        {store?.slug && (
          <section className="abt-ad-section">
            <AdPlacement
              storeSlug={store.slug}
              placement={AD_PLACEMENTS.ABOUT_BANNER}
              showAdvertiseBlurb={true}
            />
          </section>
        )}

        {/* ── QUOTE / MISSION DIVIDER ── */}
        <section
          className="abt-quote-section"
          style={{ backgroundColor: backgroundColor }}
        >
          <div className="abt-quote-inner">
            <div className="abt-quote-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill={textColor} stroke="none">
                <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C9.591 11.69 11 13.166 11 15c0 1.933-1.567 3.5-3.5 3.5-1.29 0-2.43-.696-2.917-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311C19.591 11.69 21 13.166 21 15c0 1.933-1.567 3.5-3.5 3.5-1.29 0-2.43-.696-2.917-1.179z"/>
              </svg>
            </div>
            <p className="abt-quote-text" style={{ color: textColor }}>
              We believe that every customer deserves an exceptional experience, and every product should reflect the care and passion that went into creating it.
            </p>
            <p className="abt-quote-author" style={{ color: primaryColor }}>
              — {store?.name || 'Our Team'}
            </p>
          </div>
        </section>

        {/* ── VALUES ── */}
        <section
          className="abt-values"
          style={{
            backgroundColor: `${borderColor}44`,
          }}
        >
          <div className="abt-container">
            <div className="abt-values-header">
              <div className="abt-story-label" style={{
                color: primaryColor,
                justifyContent: 'center',
              }}>
                <span className="abt-story-label-line" style={{ backgroundColor: primaryColor }} />
                What We Stand For
              </div>
              <h2 className="abt-values-heading" style={{ color: textColor }}>
                Our Values
              </h2>
              <p className="abt-values-sub" style={{ color: mutedColor }}>
                The principles that guide everything we do.
              </p>
            </div>

            <div className="abt-values-grid">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="abt-value-card"
                  style={{
                    backgroundColor: backgroundColor,
                    borderColor: borderColor,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    '--accent': primaryColor,
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: primaryColor,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }} className="abt-value-card-bar" />
                  <div
                    className="abt-value-icon"
                    style={{
                      backgroundColor: `${primaryColor}10`,
                      color: primaryColor,
                    }}
                  >
                    {renderIcon(value.icon)}
                  </div>
                  <h3 className="abt-value-title" style={{ color: textColor }}>
                    {value.title}
                  </h3>
                  <p className="abt-value-desc" style={{ color: mutedColor }}>
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FOOTER CTA ── */}
        <section
          className="abt-footer-cta"
          style={{ backgroundColor: backgroundColor }}
        >
          <div className="abt-footer-cta-inner" style={{
            background: primaryColor,
          }}>
            <h2 className="abt-footer-cta-heading">
              Ready to explore what we offer?
            </h2>
            <p className="abt-footer-cta-text">
              Discover products crafted with care and backed by our commitment to quality.
            </p>
            <button
              className="abt-footer-cta-btn"
              style={{ color: primaryColor }}
              onClick={() => navigate(getStorePath(aboutCtaLink))}
            >
              {aboutCtaText || 'Browse Our Collection'}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </section>

      </div>
    </>
  );
};

export default AboutPage;
